import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { COLORS } from '@/ui-library/design-system';
import { schProcessCalendarEvents } from '../utils/schProcessCalendarEvents';
import { SchProcessAvailability } from '../utils/SchProcessAvailability.helper';
import { getSelfAvailableSlots } from './getSelfAvailableSlots';
import isEqual from 'lodash.isequal';
import { useDateFormat } from '@/hooks';
import { SchedulerDetailsTypes } from '../../SchedulerDetails.types';
import { schedulerDetailsUtils } from '../../utils/schedulerDetails.utils';

interface UseSelfScheduleLogicProps {
  dataForAllWeeks: { [weekRange: string]: any };
  meetingDataForAllWeeks: { [weekRange: string]: any };
  interviewDataRef: { current: SchedulerDetailsTypes.InterviewData };
  calendarRef: React.RefObject<any>;
  isLoading: boolean;
  currentWeekRange: string;
  registerForUpdates: (
    key: string,
    callback: (data?: any) => void
  ) => () => void;
  updateSharedData: (
    data: Partial<SchedulerDetailsTypes.InterviewData>
  ) => void;
  isEdit: boolean;
}

const getMinimumNoticeTimeHours = (minimumNotice: any): number => {
  if (!minimumNotice) return 0;

  if (minimumNotice.value && minimumNotice.type) {
    // New nested structure
    return minimumNotice.type === 'days'
      ? minimumNotice.value * 24
      : minimumNotice.value;
  }

  return 0;
};

export const useSelfScheduleLogic = ({
  dataForAllWeeks = {},
  meetingDataForAllWeeks = {},
  interviewDataRef,
  calendarRef,
  isLoading,
  currentWeekRange,
  registerForUpdates,
  updateSharedData,
  isEdit,
}: UseSelfScheduleLogicProps) => {
  // Local state to track changes independently
  const [localInterviewers, setLocalInterviewers] = useState<any[]>(
    interviewDataRef.current.interviewers
  );
  const [userMeetingRooms, setUserMeetingRooms] = useState<any[]>(
    interviewDataRef.current.meetingRooms
  );
  const [localColorCodes, setLocalColorCodes] = useState(
    interviewDataRef.current.colorCodes
  );
  const [localDatesSet, setLocalDatesSet] = useState(
    interviewDataRef.current.datesSet
  );
  const [localBookedSlot, setLocalBookedSlot] = useState(
    interviewDataRef.current.bookedSlot
  );
  const [localInterviewKind, setLocalInterviewKind] = useState(
    interviewDataRef.current.interviewKind
  );
  const [localSchedulerState, setLocalSchedulerState] = useState(
    interviewDataRef.current.schedulerState || {}
  );
  const [localUserCalendarInfo, setLocalUserCalendarInfo] = useState(
    interviewDataRef.current.userCalendarInfo || {}
  );
  const [meetingRoomCalendarInfo, setMeetingRoomCalendarInfo] = useState(
    interviewDataRef.current.roomCalendarInfo
  );
  const [localMinimumNoticeTimeHours, setLocalMinimumNoticeTimeHours] =
    useState(
      getMinimumNoticeTimeHours(interviewDataRef.current.minimum_notice_config)
    );
  const [localMinimumNoticeEnabled, setLocalMinimumNoticeEnabled] = useState(
    interviewDataRef.current.minimum_notice_enabled
  );

  const [previousDuration, setPreviousDuration] = useState(
    interviewDataRef.current.bookedSlot?.duration || 30
  );

  // --- Add more robust check for data completeness ---
  // This assumes isLoading reflects the status of ALL necessary data.
  // If not, a more specific check based on the actual data structure is needed.
  const isDataConsideredComplete = useMemo(() => {
    if (isLoading) return false; // Still loading

    // Check only the current week and relevant interviewers
    if (!localInterviewers || localInterviewers.length === 0) return false; // No interviewers selected

    // Ensure the current week range prop is valid and present in the data keys
    if (
      !currentWeekRange ||
      !dataForAllWeeks ||
      !dataForAllWeeks[currentWeekRange]
    ) {
      return false;
    }

    // Now, check if all required interviewers have complete data *for this specific week*
    const weekData = dataForAllWeeks[currentWeekRange];
    const requiresAvailability = localDatesSet?.timeZone !== 'local'; // Check if availability fetch was expected

    for (const id of localInterviewers) {
      const normalizedId = String(id);
      const interviewerWeekData = weekData[normalizedId];

      const isAvailabilityOk =
        !requiresAvailability ||
        (interviewerWeekData && interviewerWeekData.availability !== undefined);
      const isEventsOk =
        interviewerWeekData &&
        interviewerWeekData.busyEvents !== undefined &&
        interviewerWeekData.calenderConnected !== undefined;

      if (!isAvailabilityOk || !isEventsOk) {
        return false; // Data not ready for this interviewer in the current week
      }
    }

    return true;
  }, [
    isLoading,
    localInterviewers,
    dataForAllWeeks,
    localDatesSet?.timeZone,
    currentWeekRange,
  ]);

  useEffect(() => {
    if (
      localBookedSlot?.duration !== undefined &&
      previousDuration !== localBookedSlot.duration
    ) {
      setFormValuesChanged(true);
      setPreviousDuration(localBookedSlot.duration);
    }
  }, [localBookedSlot?.duration, previousDuration]);

  // Add a flag to track interviewer changes more reliably
  const prevInterviewersRef = useRef<any[]>(localInterviewers);
  const [formValuesChanged, setFormValuesChanged] = useState(false);

  // Check for interviewer changes whenever localInterviewers changes
  useEffect(() => {
    const changed = !isEqual(prevInterviewersRef.current, localInterviewers);
    if (changed) {
      setFormValuesChanged(true);
      prevInterviewersRef.current = [...localInterviewers];
    }
  }, [localInterviewers]);

  // Registering independent updates for each field
  useEffect(() => {
    const unregisterInterviewers = registerForUpdates(
      'interviewers',
      (data) => {
        const { interviewers = [] } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setLocalInterviewers(interviewers);
      }
    );

    const unregisterColorCodes = registerForUpdates('colorCodes', (data) => {
      const { colorCodes = [] } =
        data?.fullData ?? interviewDataRef.current ?? {};
      setLocalColorCodes(colorCodes);
    });

    const unregisterDatesSet = registerForUpdates('datesSet', (data) => {
      const { datesSet = {} } =
        data?.fullData ?? interviewDataRef.current ?? {};
      setLocalDatesSet(datesSet);
    });

    const unregisterBookedSlot = registerForUpdates('bookedSlot', (data) => {
      const { bookedSlot = {} } =
        data?.fullData ?? interviewDataRef.current ?? {};
      setLocalBookedSlot(bookedSlot);
      setFormValuesChanged(true);
    });

    const unregisterInterviewKind = registerForUpdates(
      'interviewKind',
      (data) => {
        const { interviewKind } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setLocalInterviewKind(interviewKind);
        setFormValuesChanged(true);
      }
    );

    const unregisterUserCalendarInfo = registerForUpdates(
      'userCalendarInfo',
      (data) => {
        const { userCalendarInfo = {} } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setLocalUserCalendarInfo(userCalendarInfo);
      }
    );

    const unregisterSchedulerState = registerForUpdates(
      'schedulerState',
      (data) => {
        const { schedulerState = {} } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setLocalSchedulerState(schedulerState);
      }
    );

    // Add listener for resetAllSlots flag
    const unregisterResetAllSlots = registerForUpdates(
      'resetAllSlots',
      (data) => {
        const { resetAllSlots = false } =
          data?.fullData ?? interviewDataRef.current ?? {};
        if (resetAllSlots) {
          setFormValuesChanged(true);
        }
      }
    );

    const unregisterUserMeetingRooms = registerForUpdates(
      'meetingRooms',
      (data) => {
        const { meetingRooms } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setUserMeetingRooms(meetingRooms);
      }
    );

    const unregisterMeetingRoomCalendarInfo = registerForUpdates(
      'roomCalendarInfo',
      (data) => {
        const { roomCalendarInfo } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setMeetingRoomCalendarInfo(roomCalendarInfo);
      }
    );

    const unregisterMinimumNoticeTimeHours = registerForUpdates(
      'minimum_notice_config',
      (data) => {
        const { minimum_notice_config } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setLocalMinimumNoticeTimeHours(
          getMinimumNoticeTimeHours(minimum_notice_config)
        );
      }
    );

    const unregisterMinimumNoticeEnabled = registerForUpdates(
      'minimum_notice_enabled',
      (data) => {
        const { minimum_notice_enabled } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setLocalMinimumNoticeEnabled(minimum_notice_enabled);
      }
    );

    return () => {
      unregisterInterviewers();
      unregisterColorCodes();
      unregisterDatesSet();
      unregisterBookedSlot();
      unregisterInterviewKind();
      unregisterSchedulerState();
      unregisterResetAllSlots();
      unregisterUserCalendarInfo();
      unregisterUserMeetingRooms();
      unregisterMeetingRoomCalendarInfo();
      unregisterMinimumNoticeTimeHours();
      unregisterMinimumNoticeEnabled();
    };
  }, [
    registerForUpdates,
    interviewDataRef,
    currentWeekRange,
    localInterviewKind,
  ]);

  const timezone = localDatesSet?.timeZone || moment.tz.guess();

  const { dateFormat } = useDateFormat();

  // Step 1: Data Preparation (filtered data, availability, and slot generation conditions)
  const preparedData = useMemo(() => {
    if (!isDataConsideredComplete) {
      return {
        filteredDataForAllWeeks: {},
        interviewersAvailability: [],
        weekAvailability: {},
        shouldGenerateSlots: false,
      };
    }

    const filteredDataForAllWeeks: { [weekRange: string]: any } = {};
    Object.keys(dataForAllWeeks).forEach((weekRange) => {
      filteredDataForAllWeeks[weekRange] = {};
      localInterviewers.forEach((id) => {
        if (dataForAllWeeks[weekRange][id]) {
          filteredDataForAllWeeks[weekRange][id] =
            dataForAllWeeks[weekRange][id];
        }
      });
      if (Object.keys(filteredDataForAllWeeks[weekRange]).length === 0) {
        delete filteredDataForAllWeeks[weekRange];
      }
    });

    const filteredMeetingDataForAllWeeks: { [weekRange: string]: any } = {};
    Object.keys(meetingDataForAllWeeks).forEach((weekRange) => {
      filteredMeetingDataForAllWeeks[weekRange] = {};
      userMeetingRooms.forEach((id) => {
        if (meetingDataForAllWeeks[weekRange][id]) {
          filteredMeetingDataForAllWeeks[weekRange][id] =
            meetingDataForAllWeeks[weekRange][id];
        }
      });
      if (Object.keys(filteredMeetingDataForAllWeeks[weekRange]).length === 0) {
        delete filteredMeetingDataForAllWeeks[weekRange];
      }
    });

    const userCalendarInfo = interviewDataRef.current.userCalendarInfo || {};
    const interviewersAvailability = localInterviewers.map((id) => {
      const userInfo = userCalendarInfo[id];
      const hasCalendarConnected = userInfo?.isCalendarConnected === true;
      const hasEvents = userInfo?.zeroEventsByRange
        ? !Object.values(userInfo.zeroEventsByRange).every(
            (zero) => zero === true
          )
        : false;
      return { id, calendar_available: hasCalendarConnected, hasEvents };
    });

    const meetingRoomCalendarInfo =
      interviewDataRef.current.roomCalendarInfo || {};
    const meetingRoomsAvailability = userMeetingRooms.map((id) => {
      const roomInfo = meetingRoomCalendarInfo[id];
      const hasCalendarConnected = roomInfo?.isCalendarConnected === true;
      const hasEvents = roomInfo?.zeroEventsByRange
        ? !Object.values(roomInfo.zeroEventsByRange).every(
            (zero) => zero === true
          )
        : false;
      return { id, calendar_available: hasCalendarConnected, hasEvents };
    });

    const weekAvailability: { [weekRange: string]: boolean } = {};
    Object.keys(dataForAllWeeks).forEach((weekRange) => {
      if (localInterviewKind === 'one_on_one') {
        weekAvailability[weekRange] = interviewersAvailability.some(
          (interviewer) => interviewer.calendar_available
        );
      } else if (localInterviewKind === 'panel') {
        weekAvailability[weekRange] = interviewersAvailability.every(
          (interviewer) => interviewer.calendar_available
        );
      }
    });

    let shouldGenerateSlots = false;
    if (localInterviewKind === 'one_on_one') {
      shouldGenerateSlots = interviewersAvailability.some(
        (interviewer) => interviewer.calendar_available
      );
    } else if (localInterviewKind === 'panel') {
      shouldGenerateSlots = interviewersAvailability.every(
        (interviewer) => interviewer.calendar_available
      );
    }

    const newFilteredDataForAllWeeks = {};
    Object.keys(filteredDataForAllWeeks).forEach((weekRange) => {
      newFilteredDataForAllWeeks[weekRange] = {
        ...filteredDataForAllWeeks[weekRange],
        ...filteredMeetingDataForAllWeeks[weekRange],
      };
    });

    return {
      filteredDataForAllWeeks: newFilteredDataForAllWeeks,
      interviewersAvailability: [
        ...interviewersAvailability,
        ...meetingRoomsAvailability,
      ],
      weekAvailability,
      shouldGenerateSlots,
    };
  }, [
    dataForAllWeeks,
    meetingDataForAllWeeks,
    interviewDataRef,
    localInterviewers,
    localInterviewKind,
    userMeetingRooms,
    isDataConsideredComplete,
  ]);

  // Step 2: Availability, Events, and Scheduler State Updates
  const availabilityAndEvents = useMemo(() => {
    if (isLoading) {
      return {
        eventsByWeek: {},
        userAvailability: {},
        updatedSchedulerState: {},
        hasSchedulerStateChanges: false,
        slotResults: { availableSlots: [], fullDayEventDays: {} },
        allDaysByWeek: {},
        combinedAllEvents: [],
      };
    }

    const { filteredDataForAllWeeks, shouldGenerateSlots } = preparedData;

    // Clone the existing scheduler state to modify it without mutating the original
    // Also check interviewDataRef.current.schedulerState in case it was just updated
    const latestSchedulerState =
      interviewDataRef.current.schedulerState || localSchedulerState;
    let updatedSchedulerState: SchedulerDetailsTypes.SchedulerState = {
      ...latestSchedulerState,
    };

    // Flag to track if the scheduler state has changed, triggered by form changes or duration updates
    let hasSchedulerStateChanges = formValuesChanged;

    // Process calendar events for each week to identify all events and availability conflicts
    const eventsByWeek: {
      [weekRange: string]: {
        allEvents: any[];
        nonAvailabilityEvents: any[];
        hasAllDayEvent: boolean;
      };
    } = {};
    Object.keys(filteredDataForAllWeeks).forEach((weekRange) => {
      eventsByWeek[weekRange] = schProcessCalendarEvents({
        dataForCurrentWeek: filteredDataForAllWeeks[weekRange],
        // @ts-ignore
        colorCodes: localColorCodes,
        // @ts-ignore
        COLORS,
        timezone,
        dateFormat,
      });
    });

    // Calculate common availability across interviewers, considering the interview type
    const userAvailability = SchProcessAvailability({
      input: filteredDataForAllWeeks,
      timezone,
      isOneOnOne: localInterviewKind === 'one_on_one',
      userMeetingRooms,
    });

    // Helper function to normalize week range keys
    // Handles both week range strings (e.g., "Jan 4 – 10, 2026") and single date strings (e.g., "Jan 1, 2026")
    const normalizeWeekRange = (weekRange: string): string => {
      const parts = weekRange.split(' – ');
      const startStr = parts[0];
      const endStr = parts[1] || '';

      // If it's a single date string (no ' – ' separator), use the common utility
      if (!endStr) {
        return schedulerDetailsUtils.normalizeWeekRangeFromDate(
          startStr,
          timezone
        );
      }

      // Extract year from end part (e.g., "10, 2026" -> "2026" or "Jan 3, 2026" -> "2026")
      let year: number | null = null;
      if (endStr.includes(',')) {
        const yearMatch = endStr.match(/, (\d{4})/);
        if (yearMatch) {
          year = parseInt(yearMatch[1]);
        }
      }

      // Parse start date with year if available
      let weekStart: moment.Moment;
      if (year && !startStr.includes(',')) {
        // Start doesn't have year, use year from end
        const dateStr = `${startStr}, ${year}`;
        weekStart = moment.tz(dateStr, 'MMM D, YYYY', timezone).startOf('week');
      } else if (startStr.includes(',')) {
        // Start has year
        weekStart = moment
          .tz(startStr, 'MMM D, YYYY', timezone)
          .startOf('week');
      } else {
        // No year available, use default parsing (fallback)
        weekStart = moment.tz(startStr, 'MMM D', timezone).startOf('week');
      }

      const weekEnd = weekStart.clone().endOf('week');
      return schedulerDetailsUtils.formatWeekRange(weekStart, weekEnd);
    };

    // Initialize or update the scheduler state for each week
    Object.keys(dataForAllWeeks).forEach((weekRange) => {
      // Normalize the weekRange key to prevent malformed keys like "Mar 30 – 5, 2025"
      const normalizedWeekRange = normalizeWeekRange(weekRange);

      // Check latest state from ref (in case it was just updated)
      const latestSchedulerState =
        interviewDataRef.current.schedulerState || localSchedulerState;

      const durationChanged = localBookedSlot?.duration !== previousDuration;
      const existingWeekState =
        updatedSchedulerState[normalizedWeekRange] ||
        latestSchedulerState[normalizedWeekRange] ||
        latestSchedulerState[weekRange] ||
        localSchedulerState[normalizedWeekRange] ||
        localSchedulerState[weekRange];

      const willReinitialize =
        !existingWeekState || formValuesChanged || durationChanged;

      // Reinitialize the week if it's new, or if form values or duration changed
      if (willReinitialize) {
        const startPart = weekRange.split(' – ')[0];
        const endPart = weekRange.split(' – ')[1] || '';

        // Extract year from endPart if available
        let year: number | null = null;
        if (endPart.includes(',')) {
          const yearMatch = endPart.match(/, (\d{4})/);
          if (yearMatch) {
            year = parseInt(yearMatch[1]);
          }
        }

        // Parse startPart with year if available
        let weekStart: moment.Moment;
        if (year && !startPart.includes(',')) {
          // Start doesn't have year, use year from end
          const dateStr = `${startPart}, ${year}`;
          weekStart = moment
            .tz(dateStr, 'MMM D, YYYY', timezone)
            .startOf('week');
        } else if (startPart.includes(',')) {
          // Start has year
          weekStart = moment
            .tz(startPart, 'MMM D, YYYY', timezone)
            .startOf('week');
        } else {
          // Fallback: no year available
          weekStart = moment.tz(startPart, 'MMM D', timezone).startOf('week');
        }
        const days: {
          [dateKey: string]: {
            isSelected: boolean;
            slotsGenerated: SchedulerDetailsTypes.AvailableSlot[];
            customSlots: SchedulerDetailsTypes.AvailableSlot[];
            removedSlots: string[];
            backendSlots?: SchedulerDetailsTypes.AvailableSlot[];
          };
        } = {};

        for (let i = 0; i < 7; i++) {
          const dateKey = weekStart
            .clone()
            .add(i, 'days')
            .format(dateFormat?.label);

          // Retain isSelected and removedSlots, clear slotsGenerated
          // Check updatedSchedulerState first, then latest from ref, then local (check both normalized and original keys)
          const latestSchedulerState =
            interviewDataRef.current.schedulerState || localSchedulerState;
          const prevDay =
            updatedSchedulerState[normalizedWeekRange]?.days[dateKey] ||
            latestSchedulerState[normalizedWeekRange]?.days[dateKey] ||
            latestSchedulerState[weekRange]?.days[dateKey] ||
            localSchedulerState[normalizedWeekRange]?.days[dateKey] ||
            localSchedulerState[weekRange]?.days[dateKey];

          // Update customSlots with new duration if needed
          let updatedCustomSlots = prevDay?.customSlots ?? [];
          if (
            (durationChanged || formValuesChanged) &&
            prevDay?.customSlots?.length > 0 &&
            localBookedSlot?.duration
          ) {
            updatedCustomSlots = prevDay.customSlots.map((slot) => {
              // Parse the start time
              const startTime = moment(slot.start);

              // Calculate new end time based on new duration (in minutes)
              const newEndTime = startTime
                .clone()
                .add(localBookedSlot.duration, 'minutes');

              // Extract just the time portion for the ID (e.g., "17:30-18:00")
              const startTimeStr = startTime.format('HH:mm');
              const newEndTimeStr = newEndTime.format('HH:mm');
              const dateStr = startTime.format(dateFormat?.label);

              // Create updated ID with new time range
              const newId = `custom${dateStr}-${startTimeStr}-${newEndTimeStr}`;

              // Return updated slot with new end time and ID
              const updatedSlot = {
                ...slot,
                id: newId,
                end: newEndTime.toISOString(),
                title: `Available (${localBookedSlot.duration} min)`,
                extendedProps: {
                  ...slot.extendedProps,
                  description: `Available for self-scheduling (${localBookedSlot.duration} min)`,
                },
              };

              return updatedSlot;
            });
          }

          const finalCustomSlots =
            isEdit && !durationChanged // In edit case, preserve custom slots unless duration changed
              ? prevDay?.customSlots ?? []
              : durationChanged || formValuesChanged
              ? updatedCustomSlots
              : prevDay?.customSlots ?? [];

          days[dateKey] = {
            isSelected: prevDay?.isSelected ?? false, // Preserve selection state
            slotsGenerated: [], // Always clear slotsGenerated for regeneration
            customSlots: finalCustomSlots, // Update or preserve custom slots
            removedSlots: prevDay?.removedSlots ?? [], // Preserve removed slots
            ...(prevDay && (prevDay as any)?.backendSlots
              ? { backendSlots: (prevDay as any)?.backendSlots }
              : {}), // Preserve backendSlots if present
          };
        }

        // Check both normalized and original keys for isAllChecked
        const existingState =
          localSchedulerState[normalizedWeekRange] ||
          localSchedulerState[weekRange];
        updatedSchedulerState[normalizedWeekRange] = {
          // NEW: Use normalized key
          isAllChecked: existingState?.isAllChecked ?? false,
          days,
        };
        // NEW: Clean up any malformed key if it exists
        if (
          weekRange !== normalizedWeekRange &&
          updatedSchedulerState[weekRange]
        ) {
          delete updatedSchedulerState[weekRange];
        }
        hasSchedulerStateChanges = true;
      } else {
        // Always clear slotsGenerated for regeneration
        const weekStateBefore = updatedSchedulerState[normalizedWeekRange] ||
          updatedSchedulerState[weekRange] || { isAllChecked: false, days: {} };

        updatedSchedulerState[normalizedWeekRange] = weekStateBefore;
        updatedSchedulerState[normalizedWeekRange].days = Object.fromEntries(
          Object.entries(updatedSchedulerState[normalizedWeekRange].days).map(
            ([dateKey, day]) => {
              return [dateKey, { ...day, slotsGenerated: [] }];
            }
          )
        );

        // NEW: Clean up any malformed key if it exists
        if (
          weekRange !== normalizedWeekRange &&
          updatedSchedulerState[weekRange]
        ) {
          delete updatedSchedulerState[weekRange];
        }
      }
    });

    // Preserve weeks from schedulerState that have custom slots but aren't in dataForAllWeeks
    Object.keys(localSchedulerState).forEach((weekRange) => {
      const normalizedWeekRange = normalizeWeekRange(weekRange);
      // Only preserve if it's not already processed and has custom slots
      if (
        !updatedSchedulerState[normalizedWeekRange] &&
        !dataForAllWeeks[weekRange]
      ) {
        const weekState = localSchedulerState[weekRange];
        const hasCustomSlots = Object.values(weekState?.days || {}).some(
          (day: any) => day?.customSlots?.length > 0
        );
        if (hasCustomSlots) {
          updatedSchedulerState[normalizedWeekRange] = {
            ...weekState,
            days: { ...weekState.days },
          };
        }
      }
    });

    const allDaysByWeek: { [weekRange: string]: string[] } = {};
    Object.keys(updatedSchedulerState).forEach((weekRange) => {
      const days = Object.keys(updatedSchedulerState[weekRange].days)
        .map((dateKey) => {
          const parsedDate = moment(dateKey, dateFormat?.label, true).tz(
            timezone
          );
          if (!parsedDate.isValid()) {
            return null;
          }
          return parsedDate.toISOString();
        })
        .filter((isoString): isoString is string => isoString !== null);
      allDaysByWeek[weekRange] = days;
    });

    const combinedAllEvents = Object.values(eventsByWeek).flatMap(
      (e) => e.allEvents
    );

    const interviewersForSlotGen = localInterviewers.map((id) => String(id)); // Convert all IDs to strings

    // NEW: Extract individual availability data for each interviewer
    const individualAvailability: {
      [interviewerId: string | number]: { start: string; end: string }[];
    } = {};

    // Extract individual availability from dataForAllWeeks
    Object.keys(filteredDataForAllWeeks).forEach((weekRange) => {
      const weekData = filteredDataForAllWeeks[weekRange];
      Object.keys(weekData).forEach((interviewerId) => {
        const interviewerData = weekData[interviewerId];
        if (interviewerData?.availability) {
          if (!individualAvailability[interviewerId]) {
            individualAvailability[interviewerId] = [];
          }
          individualAvailability[interviewerId].push(
            ...interviewerData.availability
          );
        }
      });
    });

    // Generate slots if conditions are met, relying on availability even without events
    const slotResults = shouldGenerateSlots
      ? getSelfAvailableSlots({
          individualAvailability,
          allEvents: combinedAllEvents,
          duration: localBookedSlot?.duration,
          selectedDays: Object.values(allDaysByWeek).flat(),
          timezone,
          isOneOnOne: localInterviewKind === 'one_on_one',
          interviewers: [...interviewersForSlotGen, ...userMeetingRooms],
          userMeetingRooms: userMeetingRooms,
          userCalendarInfo: {
            ...localUserCalendarInfo,
            ...meetingRoomCalendarInfo,
          },
          dateFormat,
          minimumNoticeTimeHours: localMinimumNoticeTimeHours,
          minimumNoticeEnabled: localMinimumNoticeEnabled,
        })
      : { availableSlots: [], fullDayEventDays: {} };

    // Populate generated slots into the scheduler state
    slotResults.availableSlots.forEach((slot) => {
      const dateKey = moment(slot.start).tz(timezone).format(dateFormat?.label);
      const slotDate = moment(slot.start).tz(timezone);
      // Pass the full date string with year to normalizeWeekRange
      const slotDateFormatted = slotDate.format('MMM D, YYYY');
      const normalizedWeekRange = normalizeWeekRange(slotDateFormatted);

      if (updatedSchedulerState[normalizedWeekRange]?.days[dateKey]) {
        const day = updatedSchedulerState[normalizedWeekRange].days[dateKey];

        // Prevent adding generated slots that overlap with existing custom slots
        const existingCustomSlots = day.customSlots || [];
        const hasCustomSlotAtSameTime = existingCustomSlots.some(
          (customSlot) => {
            // Check if any custom slot has the same time as this generated slot
            const customTimeKey = customSlot.id.split('custom')[1];
            return customTimeKey === slot.id;
          }
        );

        if (hasCustomSlotAtSameTime) {
          return; // Skip adding this generated slot
        }

        // Always add new slots since slotsGenerated was cleared
        day.slotsGenerated.push(slot);
        hasSchedulerStateChanges = true;
      } else {
        // NEW: Create a new week with normalized key if it doesn't exist
        const weekStart = slotDate.clone().startOf('week');
        const newNormalizedWeekRange = normalizeWeekRange(
          weekStart.format('MMM D')
        );
        if (!updatedSchedulerState[newNormalizedWeekRange]) {
          const days: { [dateKey: string]: any } = {};
          for (let i = 0; i < 7; i++) {
            const currentDay = weekStart.clone().add(i, 'days');
            const newDateKey = currentDay.format(dateFormat?.label);
            days[newDateKey] = {
              isSelected: false,
              slotsGenerated: newDateKey === dateKey ? [slot] : [],
              customSlots: [],
              removedSlots: [],
            };
          }
          updatedSchedulerState[newNormalizedWeekRange] = {
            isAllChecked: false,
            days,
          };
          hasSchedulerStateChanges = true;
        } else {
          updatedSchedulerState[newNormalizedWeekRange].days[
            dateKey
          ].slotsGenerated.push(slot);
          hasSchedulerStateChanges = true;
        }
      }
    });

    const canProcessBackendSlots =
      isEdit &&
      !isLoading &&
      Array.isArray(userAvailability[currentWeekRange]) &&
      userAvailability[currentWeekRange].length > 0;

    const processBackendSlotsForDay = (day: any) => {
      if (!day.backendSlots || day.backendSlots.length === 0) return;

      const keepSlots: any[] = [];
      const removeSlots: any[] = [];

      day.slotsGenerated.forEach((slot: any) => {
        const match = day.backendSlots.some(
          (b: any) =>
            moment(slot.start).isSame(b.start, 'millisecond') &&
            moment(slot.end).isSame(b.end, 'millisecond')
        );
        if (match) {
          keepSlots.push(slot);
        } else {
          removeSlots.push(slot);
        }
      });

      day.slotsGenerated = keepSlots;
      day.removedSlots = Array.from(
        new Set([
          ...(day.removedSlots || []),
          ...removeSlots.map((slot: any) => slot.id),
        ])
      );
      day.isSelected = true;
      day.backendSlots = undefined;
    };

    if (canProcessBackendSlots) {
      Object.values(updatedSchedulerState).forEach((week: any) => {
        Object.entries(week.days).forEach(([dateKey, day]: [string, any]) => {
          processBackendSlotsForDay(day);
        });
      });
    }

    return {
      eventsByWeek,
      userAvailability,
      updatedSchedulerState,
      hasSchedulerStateChanges,
      slotResults,
      allDaysByWeek,
      combinedAllEvents,
    };
  }, [
    isLoading,
    preparedData,
    localSchedulerState,
    formValuesChanged,
    userMeetingRooms,
    localInterviewKind,
    dataForAllWeeks,
    localInterviewers,
    localBookedSlot.duration,
    timezone,
    localUserCalendarInfo,
    meetingRoomCalendarInfo,
    // interviewDataRef is a ref (stable), we access .current inside to get latest schedulerState
    interviewDataRef,
    dateFormat,
    currentWeekRange,
    localColorCodes,
    previousDuration,
    isEdit,
    localMinimumNoticeTimeHours,
    localMinimumNoticeEnabled,
  ]);

  // Step 3: Final Aggregation (events, visible slots, and counts)
  const processedData = useMemo(() => {
    if (isLoading) {
      return {
        events: [],
        hasAllDayEvent: false,
        fullDayEventDays: {},
        userAvailability: {},
        weekAvailability: preparedData.weekAvailability,
        updatedSchedulerState: {},
        hasSchedulerStateChanges: false,
        trackCountCurrentWeek: 0,
        trackCountTotal: 0,
      };
    }

    const {
      eventsByWeek,
      userAvailability,
      updatedSchedulerState,
      hasSchedulerStateChanges,
      slotResults,
    } = availabilityAndEvents;

    // Aggregate all events and slots for display
    const allEvents = Object.values(eventsByWeek).flatMap((e) => e.allEvents);
    const nonAvailabilityEvents = Object.values(eventsByWeek).flatMap(
      (e) => e.nonAvailabilityEvents
    );
    const hasAllDayEvent = Object.values(eventsByWeek).some(
      (e) => e.hasAllDayEvent
    );
    const visibleSlots: SchedulerDetailsTypes.AvailableSlot[] = [];
    let trackCountCurrentWeek = 0;
    let trackCountTotal = 0;

    // Collect visible slots based on day selection state
    Object.entries(updatedSchedulerState).forEach(([weekRange, weekState]) => {
      Object.entries(weekState.days).forEach(([dateKey, day]) => {
        if (day.isSelected) {
          const validGeneratedSlots = day.slotsGenerated.filter(
            (slot) => !day.removedSlots.includes(slot.id)
          );
          const validCustomSlots = day.customSlots.filter((slot) => {
            const cleanedId = slot.id.split('custom')[1];
            const isDuplicate = validGeneratedSlots.find(
              (i) => i?.id === cleanedId
            );
            return !isDuplicate;
          });
          visibleSlots.push(...validGeneratedSlots, ...validCustomSlots);
          if (weekRange === currentWeekRange) {
            trackCountCurrentWeek +=
              validGeneratedSlots.length + validCustomSlots.length;
          }
          trackCountTotal +=
            validGeneratedSlots.length + validCustomSlots.length;
        } else {
          const validCustomSlots = day.customSlots;
          visibleSlots.push(...validCustomSlots);
          if (weekRange === currentWeekRange) {
            trackCountCurrentWeek += validCustomSlots?.length;
          }
          trackCountTotal += validCustomSlots?.length;
        }
      });
    });

    // Combine all events and slots into the final events array
    const events = [...allEvents, ...nonAvailabilityEvents, ...visibleSlots];

    const newProcessedData = {
      events,
      hasAllDayEvent,
      fullDayEventDays: slotResults.fullDayEventDays,
      userAvailability,
      weekAvailability: preparedData.weekAvailability,
      updatedSchedulerState,
      hasSchedulerStateChanges,
      trackCountCurrentWeek,
      trackCountTotal,
    };

    return newProcessedData;
  }, [
    isLoading,
    availabilityAndEvents,
    preparedData.weekAvailability,
    currentWeekRange,
  ]);

  useEffect(() => {
    const {
      hasSchedulerStateChanges,
      trackCountTotal,
      updatedSchedulerState,
      trackCountCurrentWeek,
      weekAvailability,
    } = processedData || {};

    if (
      hasSchedulerStateChanges &&
      !isEqual(updatedSchedulerState, localSchedulerState)
    ) {
      // Update local state first
      setLocalSchedulerState(updatedSchedulerState);

      // Then update shared data
      updateSharedData({
        schedulerState: updatedSchedulerState,
        trackCountCurrentWeek: trackCountCurrentWeek,
        trackCountTotal: trackCountTotal,
        weekAvailability: weekAvailability,
      });

      // Reset the formValuesChanged flag after applying changes
      if (formValuesChanged) {
        setFormValuesChanged(false);
      }
    } else {
      // Check if weekAvailability, trackCountCurrentWeek, or trackCountTotal has changed
      const currentWeekAvailability =
        interviewDataRef.current.weekAvailability || {};
      const currentTrackCountCurrentWeek =
        interviewDataRef.current.trackCountCurrentWeek || 0;
      const currentTrackCountTotal =
        interviewDataRef.current.trackCountTotal || 0;

      const hasWeekAvailabilityChanged = !isEqual(
        weekAvailability,
        currentWeekAvailability
      );
      const hasTrackCountCurrentWeekChanged =
        trackCountCurrentWeek !== currentTrackCountCurrentWeek;
      const hasTrackCountTotalChanged =
        trackCountTotal !== currentTrackCountTotal;

      if (
        hasWeekAvailabilityChanged ||
        hasTrackCountCurrentWeekChanged ||
        hasTrackCountTotalChanged
      ) {
        updateSharedData({
          ...(hasWeekAvailabilityChanged && { weekAvailability }),
          ...(hasTrackCountCurrentWeekChanged && { trackCountCurrentWeek }),
          ...(hasTrackCountTotalChanged && { trackCountTotal }),
        });
      }
    }
  }, [
    processedData,
    localSchedulerState,
    updateSharedData,
    formValuesChanged,
    interviewDataRef,
    currentWeekRange,
  ]);

  const currentWeekState = useMemo(() => {
    const state = localSchedulerState[currentWeekRange] || {
      isAllChecked: false,
      days: {},
    };
    return state;
  }, [currentWeekRange, localSchedulerState]);

  const backgroundEvents = [
    {
      start: moment()
        ?.tz(timezone)
        ?.subtract(10, 'year')
        ?.format('YYYY-MM-DDTHH:mm:ssZ'),
      end: moment()?.tz(timezone)?.format('YYYY-MM-DDTHH:mm:ssZ'),
      display: 'background',
      classNames: 'bg-disabled-slots',
      color: 'transparent',
    },
  ];

  const minimumNoticeTimeHours = localMinimumNoticeTimeHours;
  if (
    localMinimumNoticeEnabled &&
    minimumNoticeTimeHours &&
    minimumNoticeTimeHours > 0
  ) {
    backgroundEvents.push({
      start: moment()?.tz(timezone)?.format('YYYY-MM-DDTHH:mm:ssZ'),
      end: moment()
        ?.tz(timezone)
        ?.add(minimumNoticeTimeHours, 'hours')
        ?.format('YYYY-MM-DDTHH:mm:ssZ'),
      display: 'background',
      classNames: 'bg-minimum-notice-disabled-slots',
      color: 'transparent',
    });
  }

  // Create background events to grey out times outside the common availability window
  // This shows users which time slots are outside interviewers' working hours
  const unavailableTimeBackgroundEvents: any[] = [];

  // Get the current week's availability (intersection for panel, union for one-on-one)
  const currentWeekAvailability = processedData.userAvailability[currentWeekRange] || [];

  // Check if we have interviewers selected (to determine if we should grey out when no availability)
  const hasInterviewersSelected = localInterviewers && localInterviewers.length > 0;

  if (localDatesSet?.start) {
    // Get the week's date range
    const weekStart = moment.tz(localDatesSet.start, timezone).startOf('week');

    // For each day in the week, create background events for unavailable times
    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
      const currentDay = weekStart.clone().add(dayOffset, 'days');
      const dayStart = currentDay.clone().startOf('day');
      const dayEnd = currentDay.clone().endOf('day');

      // Skip past days (already handled by the past time background event)
      const now = moment().tz(timezone);
      if (currentDay.isBefore(now, 'day')) {
        continue;
      }

      // Find availability slots that fall on this day
      const dayAvailabilitySlots: { start: moment.Moment; end: moment.Moment }[] = [];

      // Only look for availability slots if we have some availability data
      if (currentWeekAvailability?.length > 0) {
        currentWeekAvailability.forEach((slot: { start: string; end: string }) => {
          const slotStart = moment.utc(slot.start).tz(timezone);
          const slotEnd = moment.utc(slot.end).tz(timezone);

          // Check if this slot overlaps with the current day
          if (slotStart.isBefore(dayEnd) && slotEnd.isAfter(dayStart)) {
            // Clip the slot to this day's boundaries
            const effectiveStart = moment.max(slotStart, dayStart);
            const effectiveEnd = moment.min(slotEnd, dayEnd);

            if (effectiveEnd.isAfter(effectiveStart)) {
              dayAvailabilitySlots.push({
                start: effectiveStart,
                end: effectiveEnd,
              });
            }
          }
        });
      }

      // Sort availability slots by start time
      dayAvailabilitySlots.sort((a, b) => a.start.valueOf() - b.start.valueOf());

      // Create background events for gaps (unavailable times)
      let currentTime = dayStart.clone();

      dayAvailabilitySlots.forEach((slot) => {
        // If there's a gap before this availability slot, create a background event
        if (slot.start.isAfter(currentTime)) {
          unavailableTimeBackgroundEvents.push({
            start: currentTime.format('YYYY-MM-DDTHH:mm:ssZ'),
            end: slot.start.format('YYYY-MM-DDTHH:mm:ssZ'),
            display: 'background',
            classNames: 'bg-unavailable-working-hours',
            color: 'transparent',
          });
        }
        // Move current time to the end of this availability slot
        if (slot.end.isAfter(currentTime)) {
          currentTime = slot.end.clone();
        }
      });

      // If there's remaining time after the last availability slot, create a background event
      // This also handles the case when NO availability exists (entire day is greyed out)
      // Only grey out if we have interviewers selected (otherwise it's just an empty state)
      if (currentTime.isBefore(dayEnd) && hasInterviewersSelected) {
        unavailableTimeBackgroundEvents.push({
          start: currentTime.format('YYYY-MM-DDTHH:mm:ssZ'),
          end: dayEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
          display: 'background',
          classNames: 'bg-unavailable-working-hours',
          color: 'transparent',
        });
      }
    }
  }

  return {
    events: [...processedData.events, ...backgroundEvents, ...unavailableTimeBackgroundEvents],
    hasAllDayEvent: processedData.hasAllDayEvent,
    fullDayEventDays: processedData.fullDayEventDays,
    userAvailability: processedData.userAvailability,
    currentWeekState,
  };
};
